import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions
} from "@tanstack/react-query";

import {
  CheckProviderResponse,
  ForgotPasswordFormData,
  LoginPayload,
  LoginResponse,
  LogoutPayload,
  LogoutResponse,
  PasswordFormPayload,
  PasswordFormResponse,
  SsoTokenExchangePayload,
  SsoTokenRequestPayload,
  SsoTokenRequestResponse
} from "@/types/auth";
import { CustomError } from "@/utils/errors";

import {
  exchangeSsoToken,
  getAuthStatus,
  getSsoProvider,
  loginApi,
  patchChangePassword,
  postActivateUser,
  postForgotPassword,
  postLogout,
  postResetPasword,
  postVerifyToken,
  requestSsoToken,
  ssoTokenRequest
} from "./authApi";

export const AUTH_KEYS = {
  user: ["auth", "user"] as const,
  login: ["auth", "login"] as const,
  activateUser: ["auth", "activateUser"] as const,
  resetPassword: ["auth", "resetPassword"] as const,
  forgotPassword: ["auth", "forgotPassword"] as const,
  verifyToken: ["auth", "verifyToken"] as const,
  logout: ["auth", "logout"] as const,
  authStatus: ["auth", "status"] as const,
  ssoProvider: ["auth", "ssoProvider"] as const,
  ssoRequestToken: ["auth", "ssoRequestToken"] as const,
  ssoTokenExchange: ["auth", "ssoTokenExchange"] as const
} as const;

export const useLoginMutation = (
  options?: UseMutationOptions<
    LoginResponse,
    { message: CustomError },
    LoginPayload
  >
) => {
  return useMutation({
    mutationFn: (payload: LoginPayload) => loginApi(payload),
    ...options
  });
};

export const useActivateUserMutation = (
  options?: Omit<
    UseMutationOptions<
      PasswordFormResponse,
      { message: CustomError },
      PasswordFormPayload
    >,
    "mutationKey" | "mutationFn"
  >
) => {
  return useMutation({
    mutationKey: AUTH_KEYS.activateUser,
    mutationFn: postActivateUser,
    ...options
  });
};

export const useResetPasswordMutation = (
  options?: Omit<
    UseMutationOptions<
      PasswordFormResponse,
      { message: CustomError },
      PasswordFormPayload
    >,
    "mutationKey" | "mutationFn"
  >
) => {
  return useMutation({
    mutationKey: AUTH_KEYS.resetPassword,
    mutationFn: postResetPasword,
    ...options
  });
};

export const useForgotPasswordMutation = (
  options?: Omit<
    UseMutationOptions<null, { message: CustomError }, ForgotPasswordFormData>,
    "mutationKey" | "mutationFn"
  >
) => {
  return useMutation({
    mutationKey: AUTH_KEYS.forgotPassword,
    mutationFn: postForgotPassword,
    ...options
  });
};

export const useVerifyTokenQuery = (
  token: string,
  options?: Omit<
    UseQueryOptions<unknown, { message: CustomError }, string>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: AUTH_KEYS.verifyToken,
    queryFn: async () => await postVerifyToken(token),
    retry: false,
    ...options
  });
};

export const useLogoutMutation = (
  options?: Omit<
    UseMutationOptions<LogoutResponse, { message: CustomError }, LogoutPayload>,
    "mutationKey" | "mutationFn"
  >
) => {
  return useMutation({
    mutationKey: AUTH_KEYS.logout,
    mutationFn: postLogout,
    ...options
  });
};

export const useChangePasswordMutation = (
  options?: UseMutationOptions<
    null,
    CustomError,
    { currentPassword: string; newPassword: string }
  >
) =>
  useMutation({
    mutationFn: (params: { currentPassword: string; newPassword: string }) =>
      patchChangePassword(params),
    ...options
  });

/** Legacy (external IdP) lookup: keyed by the email domain the user typed. */
export const useGetSsoProviderMutation = (
  options?: UseMutationOptions<
    CheckProviderResponse,
    { message: CustomError },
    string
  >
) => {
  return useMutation({
    mutationFn: (domain: string) => getSsoProvider({ domain }),
    ...options
  });
};

/** LOKASI SSO lookup: keyed by this app's code (`orbis_li`). */
export const useGetSsoProviderByCodeMutation = (
  options?: Omit<
    UseMutationOptions<CheckProviderResponse, { message: CustomError }, string>,
    "mutationKey" | "mutationFn"
  >
) => {
  return useMutation({
    mutationKey: AUTH_KEYS.ssoProvider,
    mutationFn: (code: string) => getSsoProvider({ code }),
    ...options
  });
};

/**
 * LOKASI SSO: exchange the authorization `code` (+ PKCE verifier) for tokens at
 * the IdP.
 */
export const useSsoRequestTokenMutation = (
  options?: Omit<
    UseMutationOptions<
      SsoTokenRequestResponse,
      { message: CustomError },
      { url: string; payload: SsoTokenRequestPayload }
    >,
    "mutationKey" | "mutationFn"
  >
) => {
  return useMutation({
    mutationKey: AUTH_KEYS.ssoRequestToken,
    mutationFn: ({
      url,
      payload
    }: {
      url: string;
      payload: SsoTokenRequestPayload;
    }) => requestSsoToken(url, payload),
    ...options
  });
};

/** LOKASI SSO: relay the `id_token` to the BFF to mint the app session cookie. */
export const useSsoTokenExchangeMutation = (
  options?: Omit<
    UseMutationOptions<
      LoginResponse,
      { message: CustomError },
      SsoTokenExchangePayload
    >,
    "mutationKey" | "mutationFn"
  >
) => {
  return useMutation({
    mutationKey: AUTH_KEYS.ssoTokenExchange,
    mutationFn: exchangeSsoToken,
    ...options
  });
};

export const useSsoTokenRequestMutation = (
  options?: UseMutationOptions<
    SsoTokenRequestResponse,
    { message: CustomError },
    { requestUrl: string; payload: SsoTokenRequestPayload }
  >
) => {
  return useMutation({
    mutationFn: ({
      requestUrl,
      payload
    }: {
      requestUrl: string;
      payload: SsoTokenRequestPayload;
    }) => ssoTokenRequest(requestUrl, payload),
    ...options
  });
};

export const useGetAuthStatus = (
  options?: Omit<
    UseQueryOptions<unknown, { message: CustomError }, string>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: AUTH_KEYS.authStatus,
    queryFn: getAuthStatus,
    retry: false,
    ...options
  });
};
